import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Tag, Tile } from "@carbon/react";
import DataTable, {
  DataTableColumn,
  DataTableRow,
} from "../../../components/carbon/DataTable/DataTable";
import EmptyState from "../../../components/carbon/EmptyState/EmptyState";
import { FileReader } from "../../../components";
import { CompanyProfileResponse } from "../../../types";
import { formatDate } from "../../../utils/utils";
import { fileText } from "../companyProfileHelpers";
import InfoCard from "./InfoCard";
import StatsCard from "./StatsCard";

export interface CompanyProfileTabProps {
  companyProfile: CompanyProfileResponse;
}

const NONE = "-";

interface FileRow extends DataTableRow {
  name: string;
  fileType: ReactNode;
  uploaded: string;
  description: string;
  actions: ReactNode;
}

/**
 * The "Basic Information" tab — taxId/email/phone/annualRevenue summary, the profile-stats card,
 * and all company files. The files list is now a Carbon DataTable (Name / Type / Uploaded /
 * Description / download) instead of a card grid, and it covers every file type — the old
 * separate "Tax Documents" section is folded in via the Type column. The `Uploaded` date comes
 * from the file's `createdAt` (added to the backend's file response for this view).
 */
const OverviewTab = ({ companyProfile }: CompanyProfileTabProps) => {
  const { t } = useTranslation();
  const files = companyProfile.files ?? [];
  const deliverySites = (companyProfile.addresses ?? []).filter(
    (address) => address.isDeliverySite
  );
  const supplyCountries = companyProfile.supplyCountries ?? [];

  const fileRows: FileRow[] = files.map((file, index) => ({
    id: file.id ?? index,
    name: file.name || NONE,
    fileType: (
      <Tag type="purple" size="sm">
        {fileText(file.type)}
      </Tag>
    ),
    uploaded: file.createdAt ? formatDate(file.createdAt) || NONE : NONE,
    description: file.description || NONE,
    actions: file.fileURL ? (
      <FileReader fileKey={file.fileURL} text={t("companyProfile.table.view")} />
    ) : (
      NONE
    ),
  }));

  const fileHeaders: DataTableColumn<FileRow>[] = [
    { key: "name", header: t("companyProfile.table.name") },
    { key: "fileType", header: t("companyProfile.table.type") },
    { key: "uploaded", header: t("companyProfile.table.uploaded") },
    { key: "description", header: t("companyProfile.table.description") },
    { key: "actions", header: "" },
  ];

  return (
    <div className="company-profile__overview">
      <div className="company-profile__overview-grid">
        <Tile className="company-profile__main-info">
          <h3>{t("companyProfile.basicInformation")}</h3>
          <div className="company-profile__info-grid">
            <InfoCard
              label={t("companyProfile.taxId")}
              value={companyProfile.taxId}
            />
            <InfoCard
              label={t("companyProfile.email")}
              value={companyProfile.email}
            />
            <InfoCard
              label={t("companyProfile.phone")}
              value={companyProfile.phone}
            />
            <InfoCard
              label={t("companyProfile.annualRevenue")}
              value={
                companyProfile.annualRevenue
                  ? `$${companyProfile.annualRevenue.toLocaleString()}`
                  : t("companyProfile.notSpecified")
              }
            />
          </div>
        </Tile>

        <StatsCard
          title={t("companyProfile.profileStats")}
          stats={[
            {
              label: t("companyProfile.addresses"),
              value: companyProfile.addresses?.length || 0,
            },
            {
              label: t("companyProfile.contacts"),
              value: companyProfile.contacts?.length || 0,
            },
            {
              label: t("companyProfile.certifications"),
              value: companyProfile.certifications?.length || 0,
            },
            {
              label: t("companyProfile.files"),
              value: files.length,
            },
          ]}
        />
      </div>

      <Tile className="company-profile__supply">
        <div>
          <h3>{t("companyProfile.deliverySites")}</h3>
          {deliverySites.length > 0 ? (
            <ul>
              {deliverySites.map((site, index) => (
                <li key={site.id ?? index}>
                  {[site.street, site.city, site.state, site.country?.name]
                    .filter(Boolean)
                    .join(", ")}
                </li>
              ))}
            </ul>
          ) : (
            <p>{t("companyProfile.noDeliverySites")}</p>
          )}
        </div>
        <div>
          <h3>{t("companyProfile.supplyCountries")}</h3>
          {supplyCountries.length > 0 ? (
            <div className="company-profile__badge-row">
              {supplyCountries.map((country) => (
                <Tag key={country.id} type="cool-gray" size="sm">
                  {country.name}
                </Tag>
              ))}
            </div>
          ) : (
            <p>{t("companyProfile.noSupplyCountries")}</p>
          )}
        </div>
      </Tile>

      <Tile className="company-profile__files-section">
        <h3>{t("companyProfile.files")}</h3>
        <DataTable
          headers={fileHeaders}
          rows={fileRows}
          isLoading={false}
          empty={
            <EmptyState
              title={t("companyProfile.table.noFiles")}
              description={t("companyProfile.table.noFilesDesc")}
            />
          }
        />
      </Tile>
    </div>
  );
};

export default OverviewTab;
